<template>
  <v-app>
    <v-navigation-drawer
      v-model="drawer"
      app
      temporary
      v-if="$vuetify.breakpoint.smAndDown"
    >
      <navigation-menu :is-mobile="true">
        <router-view name="navigation" />
      </navigation-menu>
    </v-navigation-drawer>

    <app-bar-mobile @on-toggle="drawer = !drawer" v-if="!navbarHidden && $vuetify.breakpoint.smAndDown"></app-bar-mobile>

    <div class="app-layout">
      <!-- Left sidebar navigation -->
      <div class="navigation-sidebar" v-if="!navbarHidden && !$vuetify.breakpoint.smAndDown">
        <navigation-menu>
          <router-view name="navigation" />
        </navigation-menu>
      </div>

      <!-- Right content area -->
      <div class="content-area" :class="{ 'full-width': navbarHidden }">
        <v-main>
          <div class="page-flex">
            <banner></banner>
            <div class="main-ctn">
              <router-view name="tab"></router-view>
            </div>
          </div>
        </v-main>
      </div>
    </div>

    <v-snackbar v-model="snackBar.show" bottom>
      {{ snackBar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn v-bind="attrs" color="light-blue lighten-2" text @click="dissmissSnackBar()">
          {{$t('dismiss')}}
        </v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>
import { default as axios } from './utils/axios.instances' // eslint-disable-line
import { mapMutations, mapGetters } from 'vuex'
import amplitude from 'amplitude-js'
import Api from '@/api'
import Banner from '@/components/Banner'
import NavigationMenu from '@/views/Navigation/NavigationMenu'
import AppBarMobile from '@/views/Navigation/AppBarMobile'

export default {
  name: 'App',
  computed: {
    ...mapGetters(['campaigns', 'snackBar', 'currentPlan', 'isRefreshingToken', 'contract'])
  },
  components: {
    Banner,
    NavigationMenu,
    AppBarMobile
  },
  data () {
    return {
      navbarHidden: false,
      drawer: false
    }
  },
  methods: {
    ...mapMutations({
      setSelectedCampaign: 'SET_SELECTED_CAMPAIGN',
      dissmissSnackBar: 'DISSMISS_SNACK_BAR'
    }),

    initAmplitude (user) {
      const identity = new amplitude.Identify().set('plan', this.currentPlan.id).set('email', user.email)
      amplitude.getInstance().setUserId(user.id)
      amplitude.getInstance().identify(identity)
    },

    editorIframes () {
      return ['stc-frame', 'elabel-editor-frame', 'dpp-frame']
        .map((id) => document.getElementById(id))
        .filter((iframe) => iframe && iframe.contentWindow)
    },

    isFromEditorIframe (event) {
      return this.editorIframes().some((iframe) => iframe.contentWindow === event.source)
    },

    forwardTokenToIframes (token) {
      this.editorIframes().forEach((iframe) => {
        iframe.contentWindow.postMessage({ type: 'token', token: token }, '*')
        iframe.contentWindow.postMessage({ type: 'tokenUpdateCompleted', token: token }, '*')
      })
    },

    handleMessage (event) {
      console.log('msg from parent', event)
      if (event.data && event.data.type === 'token' && event.data.token) {
        this.$store.dispatch('setScantrustAuthToken', event.data.token)
        this.forwardTokenToIframes(event.data.token)
      } else if (event.data && event.data.type === 'tokenUpdateCompleted' && event.data.token) {
        this.$store.dispatch('setScantrustAuthToken', event.data.token)
        this.forwardTokenToIframes(event.data.token)
        // only call process pending request when refreshing the token on 401 error
        if (this.isRefreshingToken) {
          this.$store.dispatch('processPendingRequest', event.data.token)
        }
      }

      if (event.data.type === 'tokenUpdateRequested' && this.isFromEditorIframe(event)) {
        window.parent.postMessage({ type: 'tokenUpdateRequested' }, '*')
      }

      // Events from landing pages editor
      if (event.data.type === 'forceTokenRefresh') {
        console.log('forward to portal')
        // Forward to portal
        window.parent.postMessage({ type: 'forceTokenRefresh' }, '*')
      }
    }
  },
  async created () {
    if (Api.amplitude_key) {
      amplitude.getInstance().init(Api.amplitude_key)
      amplitude.getInstance().options.apiEndpoint = 'api2.amplitude.com'
    }
    if (this.$route.query.hidenav) {
      this.navbarHidden = true
    }

    window.addEventListener('message', this.handleMessage, false)
    axios.initAxiosInterceptor(this.$store)

    const token = this.$route.query.token || Api.uat_token
    if (token) {
      this.$store.dispatch('setScantrustAuthToken', token)
      const user = await this.$store.dispatch('loadUserProfile')
      this.$store.dispatch('loadQuotas')
      this.$store.dispatch('loadBlacklistedCodeCount')
      await this.$store.dispatch('loadUserContract', { id: user.plan?.contract_id })
      if (this.contract?.order_number) {
        this.$store.dispatch('loadSubscription')
      }
      if (user.company.external_id) {
        this.$store.dispatch('loadCustomer')
      } else if (!user.is_staff) {
        this.$store.dispatch('createCustomer', { name: `${user.first_name} ${user.last_name}` })
      }
      // load all contracts to check later if they have paused ones or stuff like that
      this.$store.dispatch('loadUserContracts')
      this.$i18n.locale = user.language
      this.initAmplitude(user)
      await this.$store.dispatch('loadCampaigns')
      this.setSelectedCampaign(this.campaigns[0])
    }
  },
  beforeDestroy () {
    // clean up listener
    window.removeEventListener('message', this.handleMessage)
  }
}
</script>

<style lang="sass">
@import '@/variables.sass'

.app-layout
  display: flex
  height: 100vh
  width: 100%

.navigation-sidebar
  background: white
  border-right: 1px solid #e0e0e0
  overflow-y: auto
  overflow: visible
  flex-shrink: 0
  background-color: $light-bg
  z-index: 10
  position: relative

.content-area
  flex: 1
  display: flex
  flex-direction: column
  min-width: 0
  position: relative
  z-index: 1

  &.full-width
    width: 100%

main
  background: white
  flex: 1

.page-flex
  display: flex
  flex-direction: column
  height: 100%

.main-ctn
  flex: 1
  min-height: 0
  width: 100%
  background: white

// Mobile responsiveness
@media (max-width: 768px)
  .navigation-sidebar
    width: 200px
    min-width: 200px

@media (max-width: 600px)
  .app-layout
    flex-direction: column

  .navigation-sidebar
    width: 100%
    height: 60px
    border-right: none
    border-bottom: 1px solid #e0e0e0

  .content-area
    flex: 1
    height: calc(100vh - 60px)

</style>
